<script setup lang="ts">
import type { PreviewItemData } from '@autobid/strapi-integration/typescript/auction/carPreview'
import type { AuctionCar } from '@autobid/ui/types/Car'
import { getError } from '@autobid/strapi-integration/utils/getError'
import Spinner from '@autobid/ui/components/common/Spinner.vue'
import { useProfile } from '@autobid/ui/composables/useProfile'
import { parseCevaTransportPayload } from '@autobid/ui/utils/car/parseTransportPayload'
import { useQuery } from '@tanstack/vue-query'

interface Props {
  car: AuctionCar | PreviewItemData
}

const props = defineProps<Props>()

const isOpen = ref(false)
const { locale, t } = useI18n()
const { data: profile } = useProfile()
const push = usePush()
const { $trpc } = useNuxtApp()
const TERMS_CONDITION_LINK = 'https://cms.autobid.de/uploads/CEVA_Group_terms_and_conditions_240901_fa5e39f96b.pdf'

const { data: calculation, isLoading: isCalculationLoading } = useQuery({
  queryKey: ['transport-ceva-calculation', locale, profile.value?.id, props.car.id],
  queryFn: async () => {
    if (!profile.value || !('category' in props.car))
      return null

    const payload = parseCevaTransportPayload({
      car: props.car,
      profile: profile.value,
    })

    const result = await $trpc.towTruck.getCevaCalculation.mutate(payload)

    if (!result) {
      throw new Error(`${t('unexpected-error')} (No result)`)
    }

    return result
  },
  onError: (error: unknown) => {
    if (!isOpen.value)
      return

    const { message, code } = getError(error)

    push.error([422, 400].includes(code) ? t(`ceva-transport-modal.error.${code}`) : message)
    isOpen.value = false
  },
  enabled: computed(() => Boolean(isOpen.value)),
  cacheTime: 1000 * 60 * 5,
})

const shouldDialogBeShown = computed(() => {
  return Boolean(isOpen.value && calculation.value)
})

/**
 * Changes format 63.4 to 63,40
 */
function formatPrice(price: string) {
  return Number.parseFloat(price).toFixed(2).replace('.', ',')
}
</script>

<template>
  <CommonTooltip :tooltip="$t('cars-list.transport-tooltip')">
    <DynamicAuctionItemListCarIcon
      variant="border"
      size="big"
      v-bind="$attrs"
      @click.stop="isOpen = true"
    >
      <Spinner v-if="isCalculationLoading && isOpen" />
      <i v-else class="ab-icon ab-icon-transport h-full" />
    </DynamicAuctionItemListCarIcon>
  </CommonTooltip>

  <CommonDialog :opened="shouldDialogBeShown" @close="isOpen = false">
    <template #title>
      {{ $t('ceva-transport-modal.title') }}
    </template>
    <template v-if="calculation" #content>
      <div class="text-lg">
        <i18n-t keypath="ceva-transport-modal.calculation-info" tag="div">
          <template #distance>
            <div class="my-2 border-l-2 border-l-primary pl-4">
              <p>{{ calculation.origin.addressFirstLine }}</p>
              <p>{{ calculation.origin.addressSecondLine }}</p>
            </div>
          </template>
          <template #destination>
            <div class="my-2 border-l-2 border-l-primary pl-4">
              <p>
                {{
                  profile?.extendedData?.addressBook?.items[
                    profile.extendedData.addressBook.vehicleDeliveryAddress
                  ].companyName
                }}
              </p>
              <p>{{ calculation.payload.destination.street }}</p>
              <p>
                {{
                  `${calculation.payload.destination.zip} ${calculation.payload.destination.city} (${calculation.payload.destination.country})`
                }}
              </p>
            </div>
          </template>
          <template #basePrice>
            <span class="font-bold">{{ formatPrice(calculation.result.basePrice) }} €</span>
          </template>
        </i18n-t>
        <p class="mt-4">
          {{ $t('ceva-transport-modal.additional-info') }}
        </p>
        <i18n-t
          class="mt-4"
          keypath="ceva-transport-modal.additional-info-2"
          tag="p"
        >
          <template #link>
            <CommonLink underline class="text-primary font-bold" :href="TERMS_CONDITION_LINK">
              {{ $t('ceva-transport-modal.link-label') }}
            </CommonLink>
          </template>
        </i18n-t>
        <p class="mt-4 italic">
          {{ $t('ceva-transport-modal.footer') }}
        </p>
      </div>
    </template>
  </CommonDialog>
</template>
